import { useEffect, useLayoutEffect, useReducer, useRef } from "react";
import { Group, InstancedMesh, Matrix4, Object3D } from "three";
import { BUILDING } from "./layout";
import { WINDOW_REGISTRY, buildWindowFrameGeometry } from "./Wall";
import { Wall, WindowPane, DoorLeaf, type Opening } from "./Wall";
import { useOptionalTexture, useStandardMaterial } from "./materials";
import {
  Reception,
  WaitingRoom,
  PrivateCubicles,
  TheoryRoom,
  SupervisorPost,
  CommsRoom,
  Sanitarios,
  Circulation,
} from "./rooms";
import { SignDecal, EmergencyLight } from "./SignDecal";

const W = BUILDING.width; // 40 (X)
const D = BUILDING.depth; // 22 (Z)
const H = BUILDING.floorHeight * BUILDING.floors; // 6.4
const FH = BUILDING.floorHeight;

/** Vanos de fachada frontal (muro sur, 40 m): puerta principal + ventanas. */
const FRONT_OPENINGS: Opening[] = [
  { x: 3.8, width: 2.4, height: 2.4 }, // puerta principal (local x = -15)
  { x: 12, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 18, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 24, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 30, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 35.5, width: 1.8, height: 1.4, sill: 0.9 },
  // segundo piso
  { x: 6, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 12, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 18, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 24, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 30, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 35.5, width: 1.8, height: 1.4, sill: FH + 0.9 },
];

const BACK_OPENINGS: Opening[] = [
  { x: 10, width: 2, height: 2.2 }, // puerta de egreso desde sala teórica
  { x: 4, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 18, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 30, width: 1.8, height: 1.4, sill: 0.9 },
  { x: 6, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 14, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 22, width: 1.8, height: 1.4, sill: FH + 0.9 },
  { x: 30, width: 1.8, height: 1.4, sill: FH + 0.9 },
];

const SIDE_OPENINGS: Opening[] = [
  { x: 9, width: 1.6, height: 2.2 }, // puerta de egreso lateral
  { x: 3, width: 1.6, height: 1.4, sill: 0.9 },
  { x: 16, width: 1.6, height: 1.4, sill: 0.9 },
  { x: 3, width: 1.6, height: 1.4, sill: FH + 0.9 },
  { x: 9.5, width: 1.6, height: 1.4, sill: FH + 0.9 },
  { x: 16, width: 1.6, height: 1.4, sill: FH + 0.9 },
];

function windowsFor(openings: Opening[], wallLength: number) {
  return openings
    .filter((o) => (o.sill ?? 0) > 0)
    .map((o, i) => ({
      key: i,
      width: o.width,
      height: o.height,
      center: o.x + o.width / 2 - wallLength / 2,
      y: (o.sill ?? 0) + o.height / 2,
    }));
}

/**
 * Edificio institucional de dos plantas (40 × 22 m, piso 3.2 m),
 * construido proceduralmente: muros con vanos reales, losa intermedia,
 * cubierta plana con parapeto y acceso peatonal cubierto.
 * Coordenadas locales: origen en el centro de la huella.
 */
export function Building() {
  const rootRef = useRef<Group>(null);
  // blanco cálido: la textura de concreto solo matiza los pisos, no los muros
  const wallMat = useStandardMaterial({ color: "#f5f2ea", roughness: 0.85 });
  const floorTex = useOptionalTexture("/assets/textures/Tiles074.jpg", [18, 10]);

  const frontWin = windowsFor(FRONT_OPENINGS, W);
  const backWin = windowsFor(BACK_OPENINGS, W);
  const sideWin = windowsFor(SIDE_OPENINGS, D);

  return (
    <group name="edificio" ref={rootRef} position={[BUILDING.center[0], 0, BUILDING.center[2]]}>
      {/* piso interior con textura de concreto */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <boxGeometry args={[W - 0.1, 0.1, D - 0.1]} />
        <meshStandardMaterial color="#d5d0c2" roughness={0.9} map={floorTex ?? undefined} />
      </mesh>

      {/* fachada sur (acceso) */}
      <Wall length={W} height={H} openings={FRONT_OPENINGS} position={[0, 0, D / 2]} material={wallMat}>
        {frontWin.map((w) => (
          <WindowPane key={w.key} width={w.width} height={w.height} position={[w.center, w.y, 0]} />
        ))}
        <DoorLeaf width={2.4} height={2.4} position={[-15, 0, 0]} />
        {/* entrada acristalada: paños fijos laterales y sobrepuerta */}
        {[-16.8, -13.2].map((x) => (
          <WindowPane key={x} width={1.2} height={2.4} position={[x, 1.2, 0]} />
        ))}
        <WindowPane width={2.4} height={0.6} position={[-15, 2.75, 0]} />
      </Wall>
      {/* fachada norte */}
      <Wall length={W} height={H} openings={BACK_OPENINGS} position={[0, 0, -D / 2]} material={wallMat}>
        {backWin.map((w) => (
          <WindowPane key={w.key} width={w.width} height={w.height} position={[w.center, w.y, 0]} />
        ))}
        <DoorLeaf width={2} height={2.2} position={[-9, 0, 0]} />
      </Wall>
      {/* fachadas oriente/occidente */}
      <Wall length={D} height={H} openings={SIDE_OPENINGS} position={[W / 2, 0, 0]} rotationY={Math.PI / 2} material={wallMat}>
        {sideWin.map((w) => (
          <WindowPane key={w.key} width={w.width} height={w.height} position={[w.center, w.y, 0]} />
        ))}
        <DoorLeaf width={1.6} height={2.2} position={[-1.8, 0, 0]} />
      </Wall>
      <Wall length={D} height={H} openings={SIDE_OPENINGS} position={[-W / 2, 0, 0]} rotationY={Math.PI / 2} material={wallMat}>
        {sideWin.map((w) => (
          <WindowPane key={w.key} width={w.width} height={w.height} position={[w.center, w.y, 0]} />
        ))}
      </Wall>

      {/* banda horizontal institucional teal a la altura de la losa */}
      {[
        { p: [0, FH + 0.1, D / 2 + 0.06] as const, s: [W + 0.12, 0.55, 0.1] as const },
        { p: [0, FH + 0.1, -D / 2 - 0.06] as const, s: [W + 0.12, 0.55, 0.1] as const },
        { p: [W / 2 + 0.06, FH + 0.1, 0] as const, s: [0.1, 0.55, D + 0.12] as const },
        { p: [-W / 2 - 0.06, FH + 0.1, 0] as const, s: [0.1, 0.55, D + 0.12] as const },
      ].map((b, i) => (
        <mesh key={i} position={[b.p[0], b.p[1], b.p[2]]}>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color="#2f6364" roughness={0.55} />
        </mesh>
      ))}

      {/* losa intermedia */}
      <mesh position={[0, FH + 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[W - 0.2, 0.3, D - 0.2]} />
        <meshStandardMaterial color="#d8d3c5" roughness={0.9} />
      </mesh>

      {/* cubierta plana con alero perimetral y sombra de alero */}
      <mesh position={[0, H + 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[W + 1, 0.3, D + 1]} />
        <meshStandardMaterial color="#b3ae9f" roughness={0.95} />
      </mesh>
      {/* junta de sombra bajo el alero */}
      {[
        { p: [0, H - 0.04, D / 2 + 0.28] as const, s: [W + 0.6, 0.08, 0.5] as const },
        { p: [0, H - 0.04, -D / 2 - 0.28] as const, s: [W + 0.6, 0.08, 0.5] as const },
        { p: [W / 2 + 0.28, H - 0.04, 0] as const, s: [0.5, 0.08, D + 0.6] as const },
        { p: [-W / 2 - 0.28, H - 0.04, 0] as const, s: [0.5, 0.08, D + 0.6] as const },
      ].map((b, i) => (
        <mesh key={i} position={[b.p[0], b.p[1], b.p[2]]}>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color="#6b675c" roughness={1} />
        </mesh>
      ))}
      {/* parapeto */}
      {[
        { p: [0, H + 0.6, D / 2 - 0.1] as const, s: [W + 0.6, 0.6, 0.2] as const },
        { p: [0, H + 0.6, -D / 2 + 0.1] as const, s: [W + 0.6, 0.6, 0.2] as const },
        { p: [W / 2 - 0.1, H + 0.6, 0] as const, s: [0.2, 0.6, D + 0.6] as const },
        { p: [-W / 2 + 0.1, H + 0.6, 0] as const, s: [0.2, 0.6, D + 0.6] as const },
      ].map((b, i) => (
        <mesh key={i} position={[b.p[0], b.p[1], b.p[2]]} castShadow>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color="#e6e1d4" roughness={0.9} />
        </mesh>
      ))}

      {/* señalización de las puertas de egreso norte y oriente [NSR-10 Título K] */}
      <group>
        <SignDecal text="SALIDA" position={[-9, 2.55, -D / 2 + 0.15]} variant="evac" width={1.2} height={0.4} />
        <EmergencyLight position={[-9, 2.95, -D / 2 + 0.15]} />
        <SignDecal text="SALIDA" position={[W / 2 - 0.15, 2.55, -1.8]} rotationY={-Math.PI / 2} variant="evac" width={1.2} height={0.4} />
        <EmergencyLight position={[W / 2 - 0.15, 2.95, -1.8]} rotationY={-Math.PI / 2} />
      </group>

      {/* pilastras de fachada (ritmo 5 m): volumen vertical que lee la escala */}
      {[-17.5, -12.5, -7.5, -2.5, 2.5, 7.5, 12.5, 17.5].flatMap((px) =>
        [D / 2 + 0.09, -D / 2 - 0.09].map((pz, j) => (
          <mesh key={`${px}-${j}`} position={[px, H / 2, pz]} castShadow>
            <boxGeometry args={[0.42, H, 0.18]} />
            <meshStandardMaterial color="#e9e4d6" roughness={0.85} />
          </mesh>
        )),
      )}
      {/* zócalo de 0.6 m en el perímetro */}
      {[
        { p: [0, 0.3, D / 2 + 0.12] as const, s: [W + 0.5, 0.6, 0.24] as const },
        { p: [0, 0.3, -D / 2 - 0.12] as const, s: [W + 0.5, 0.6, 0.24] as const },
        { p: [W / 2 + 0.12, 0.3, 0] as const, s: [0.24, 0.6, D + 0.5] as const },
        { p: [-W / 2 - 0.12, 0.3, 0] as const, s: [0.24, 0.6, D + 0.5] as const },
      ].map((b, i) => (
        <mesh key={i} position={[b.p[0], b.p[1], b.p[2]]} receiveShadow castShadow>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color="#8f8a7c" roughness={0.9} />
        </mesh>
      ))}
      {/* letrero principal de fachada sobre la marquesina */}
      <SignDecal text="CALE · IUE ENVIGADO" position={[-15, FH + 2.1, D / 2 + 0.14]} variant="plate" width={6.4} height={0.85} />
      {/* equipos de cubierta (unidades de aire) */}
      {[[-12, 2], [6, -3]].map(([px, pz], i) => (
        <group key={i} position={[px, H + 0.3, pz]}>
          <mesh castShadow>
            <boxGeometry args={[1.6, 0.8, 1.1]} />
            <meshStandardMaterial color="#9aa1a5" roughness={0.6} metalness={0.4} />
          </mesh>
          <mesh position={[0, 0.41, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[0.38, 20]} />
            <meshStandardMaterial color="#5a6064" roughness={0.5} metalness={0.5} />
          </mesh>
        </group>
      ))}
      {/* banderas: institucional (teal) y Colombia, junto al acceso */}
      {[
        { x: -19.6, colors: ["#2f6364", "#f4f2ec", "#2f6364"] },
        { x: -10.4, colors: ["#f0b429", "#2b4590", "#b03a2e"] },
      ].map((f, i) => (
        <group key={i} position={[f.x, 0, D / 2 + 1.6]}>
          <mesh position={[0, 3.2, 0]} castShadow>
            <cylinderGeometry args={[0.045, 0.06, 6.4, 8]} />
            <meshStandardMaterial color="#c9c4b6" metalness={0.7} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.15, 0]}>
            <cylinderGeometry args={[0.22, 0.26, 0.3, 10]} />
            <meshStandardMaterial color="#8f8a7c" roughness={0.9} />
          </mesh>
          {f.colors.map((c, j) => (
            <mesh key={j} position={[0.55, 5.75 - j * 0.3, 0]}>
              <planeGeometry args={[1.05, 0.3]} />
              <meshStandardMaterial color={c} roughness={0.9} side={2} />
            </mesh>
          ))}
        </group>
      ))}

      {/* acceso peatonal cubierto: marquesina 8 × 3 m sobre la puerta principal */}
      <group position={[-15, 0, D / 2]}>
        <mesh position={[0, 2.9, 1.6]} castShadow>
          <boxGeometry args={[8, 0.15, 3.2]} />
          <meshStandardMaterial color="#e8e4d8" roughness={0.7} />
        </mesh>
        {[-3.6, 3.6].map((x) => (
          <mesh key={x} position={[x, 1.45, 2.9]} castShadow>
            <cylinderGeometry args={[0.09, 0.09, 2.9, 10]} />
            <meshStandardMaterial color="#4a4f52" roughness={0.4} metalness={0.6} />
          </mesh>
        ))}
        <SignDecal text="CALE · ENVIGADO" position={[0, 2.55, 3.26]} variant="plate" width={2.6} height={0.44} />
        <EmergencyLight position={[0, 2.75, 0.3]} />
      </group>

      {/* particiones interiores de la planta baja */}
      <InteriorPartitions />

      {/* luces interiores suaves (recepción, espera, cubículos y corredor) */}
      <pointLight position={[-15, 2.9, 8]} intensity={16} distance={13} decay={2} color="#fff2dd" />
      <pointLight position={[-5, 2.9, 8]} intensity={12} distance={12} decay={2} color="#fff2dd" />
      <pointLight position={[-8, 2.9, -4]} intensity={14} distance={16} decay={2} color="#fdf6e8" />
      <pointLight position={[8.5, 2.9, 8]} intensity={12} distance={11} decay={2} color="#fff2dd" />
      <pointLight position={[-4, 2.9, 4]} intensity={10} distance={14} decay={2} color="#fdf6e8" />
      {/* paneles de techo emisivos en zonas públicas (instanciados) */}
      <CeilingPanels />
      {/* ventanas instanciadas (vidrio + marcos + antepechos) */}
      <WindowInstances rootRef={rootRef} />

      {/* habitaciones */}
      <Reception />
      <WaitingRoom />
      <PrivateCubicles />
      <TheoryRoom />
      <SupervisorPost />
      <CommsRoom />
      <Sanitarios />
      <Circulation />
    </group>
  );
}

/**
 * Dibuja todas las ventanas registradas (WINDOW_REGISTRY) con instancedMesh:
 * 1 vidrio escalado, 1 marco por variante de tamaño y 1 antepecho escalado,
 * en coordenadas locales del grupo "edificio".
 */
function WindowInstances({ rootRef }: { rootRef: React.RefObject<Group | null> }) {
  const glassRef = useRef<InstancedMesh>(null);
  const sillRef = useRef<InstancedMesh>(null);
  const frameRefs = useRef<Map<string, InstancedMesh>>(new Map());
  // El registro se llena en la fase de commit (layout effects de WindowPane),
  // después del primer render: forzar un segundo render para capturarlo.
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    bump();
  }, []);
  const specs = [...WINDOW_REGISTRY];
  const variants = [...new Map(specs.map((s) => [`${s.width}x${s.height}`, s])).values()];

  // Sin arreglo de deps: corre tras cada render (el edificio re-renderiza poco)
  // y es idempotente; garantiza matrices correctas aunque el registro llegue tarde.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.updateWorldMatrix(true, true);
    const invRoot = new Matrix4().copy(root.matrixWorld).invert();
    const dummy = new Object3D();
    const local = new Matrix4();

    const glass = glassRef.current;
    const sill = sillRef.current;
    const pos = new (Object3D)().position; // reutilizado por instancia
    const frameIndex = new Map<string, number>();
    specs.forEach((s, i) => {
      local.multiplyMatrices(invRoot, s.el.matrixWorld);
      local.decompose(dummy.position, dummy.quaternion, dummy.scale);
      pos.copy(dummy.position);
      if (glass) {
        dummy.scale.set(s.width, s.height, 1);
        dummy.updateMatrix();
        glass.setMatrixAt(i, dummy.matrix);
      }
      if (sill) {
        dummy.position.set(pos.x, pos.y, pos.z);
        dummy.translateY(-s.height / 2 - 0.08);
        dummy.translateZ(0.06);
        dummy.scale.set(s.width + 0.16, 1, 1);
        dummy.updateMatrix();
        sill.setMatrixAt(i, dummy.matrix);
      }
      const key = `${s.width}x${s.height}`;
      const frame = frameRefs.current.get(key);
      if (frame) {
        const idx = frameIndex.get(key) ?? 0;
        frameIndex.set(key, idx + 1);
        dummy.position.set(pos.x, pos.y, pos.z);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        frame.setMatrixAt(idx, dummy.matrix);
      }
    });
    if (glass) glass.instanceMatrix.needsUpdate = true;
    if (sill) sill.instanceMatrix.needsUpdate = true;
    frameRefs.current.forEach((m) => (m.instanceMatrix.needsUpdate = true));
  });

  return (
    <group name="ventanas-instanciadas">
      <instancedMesh ref={glassRef} args={[undefined, undefined, specs.length]}>
        <boxGeometry args={[1, 1, 0.03]} />
        {/* vidrio "día": básico (sin modelo de iluminación) para que nunca
            renderice negro desde interiores; la transparencia deja ver el exterior */}
        <meshBasicMaterial color="#e4edf0" transparent opacity={0.28} toneMapped={false} />
      </instancedMesh>
      <instancedMesh ref={sillRef} args={[undefined, undefined, specs.length]}>
        <boxGeometry args={[1, 0.05, 0.2]} />
        <meshStandardMaterial color="#c9c4b6" roughness={0.8} />
      </instancedMesh>
      {variants.map((v) => {
        const key = `${v.width}x${v.height}`;
        const count = specs.filter((s) => `${s.width}x${s.height}` === key).length;
        return (
          <instancedMesh
            key={key}
            ref={(m) => {
              if (m) frameRefs.current.set(key, m);
            }}
            args={[undefined, undefined, count]}
          >
            <primitive object={buildWindowFrameGeometry(v.width, v.height)} attach="geometry" />
            <meshStandardMaterial color="#4a4f52" roughness={0.45} metalness={0.55} />
          </instancedMesh>
        );
      })}
    </group>
  );
}

/** Paneles de techo emisivos instanciados (6 → 1 llamada de dibujo). */
function CeilingPanels() {
  const ref = useRef<InstancedMesh>(null);
  const spots: [number, number][] = [
    [-15, 8], [-15, 4], [-5, 8], [-5, 4], [5, 8], [5, 4],
    // cubículos y corredor
    [9, 8], [9, 4], [-15, 3.9], [-5, 3.9], [5, 3.9], [12, 8],
  ];
  useLayoutEffect(() => {
    if (!ref.current) return;
    const dummy = new Object3D();
    spots.forEach(([x, z], i) => {
      dummy.position.set(x, 3.14, z);
      dummy.updateMatrix();
      ref.current!.setMatrixAt(i, dummy.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
    // `spots` es constante del módulo de iluminación; solo se monta una vez
  }, []);
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, spots.length]}>
      <boxGeometry args={[1.2, 0.05, 0.4]} />
      <meshStandardMaterial color="#f2f0e8" emissive="#fff6e2" emissiveIntensity={1.5} />
    </instancedMesh>
  );
}

/** Particiones de planta baja (altura piso 3.2 m) con puertas interiores. */
function InteriorPartitions() {
  return (
    <group name="particiones">
      {/* frente interior: separa recepción/espera/cubículos del corredor (z = 5) */}
      <Wall
        length={33}
        height={FH}
        thickness={0.12}
        color="#cfcaba"
        openings={[
          { x: 3, width: 1.6, height: 2.2 }, // a recepción
          { x: 12, width: 1.6, height: 2.2 }, // a espera
          { x: 27, width: 1.4, height: 2.2 }, // a cubículos
        ]}
        position={[-3.5, 0, 5]}
      />
      {/* corredor → sala teórica (z = 3) */}
      <Wall
        length={24}
        height={FH}
        thickness={0.12}
        color="#cfcaba"
        openings={[{ x: 12, width: 1.8, height: 2.2 }]}
        position={[-8, 0, 3]}
      />
      {/* recepción | espera */}
      <Wall length={6} height={FH} thickness={0.12} color="#cfcaba" position={[-10, 0, 8]} rotationY={Math.PI / 2} />
      {/* espera | sanitarios-cubículos (x = 0, z 5..11) */}
      <Wall length={6} height={FH} thickness={0.12} color="#cfcaba" position={[0, 0, 8]} rotationY={Math.PI / 2} />
      {/* cubículos | corredor este (x = 13, z 3..11) */}
      <Wall length={8} height={FH} thickness={0.12} color="#cfcaba" position={[13, 0, 7]} rotationY={Math.PI / 2} />
      {/* sala teórica | comunicaciones-control (x = 4, z -11..3) */}
      <Wall
        length={14}
        height={FH}
        thickness={0.12}
        color="#cfcaba"
        openings={[{ x: 4, width: 1.4, height: 2.2 }]}
        position={[4, 0, -4]}
        rotationY={Math.PI / 2}
      />
      {/* comunicaciones | control (z = -5, x 4..13) */}
      <Wall length={9} height={FH} thickness={0.12} color="#cfcaba" position={[8.5, 0, -5]} />
    </group>
  );
}
